// bluaIQ — Material Lead Times (Bloque 3)
// Default lead times for material orders in DFW residential construction.
// Used by the Calendar module to auto-generate "📦 Pedir: [Category]" reminders
// calculated as `lifecycleTask.startDate - leadTimeDays`.
//
// Override per project by setting QuoteItem.leadTimeDays on a specific supplier quote
// (e.g. "custom windows 6 weeks from this vendor, not 4").

/**
 * Default lead times by takeoff category. Days, not weeks.
 * Confirmed with Blu (active DFW builder) for the categories marked below.
 */
export const DFW_LEAD_TIMES_DAYS: Record<string, number> = {
  FOUNDATION: 14,        // 2 weeks — concrete + rebar are commodity
  FRAMING: 14,           // 2 weeks — lumber, OSB, engineered LVL
  ROOFING: 14,           // 2 weeks — shingles standard, metal custom slower
  EXTERIOR: 21,          // 3 weeks — Hardie siding, colors can take longer
  WINDOWS: 42,           // 6 weeks — custom-order, almost always long lead
  DOORS: 21,             // 3 weeks — interior fast, exterior fiberglass slower
  PLUMBING_ROUGH: 14,    // 2 weeks — PEX, fittings, valves all commodity
  PLUMBING_FIXTURES: 21, // 3 weeks — toilets/sinks fast, freestanding tubs slow
  ELECTRICAL_ROUGH: 14,  // 2 weeks — Romex, breakers, panels commodity
  LIGHTING_FANS: 14,     // 2 weeks — Blu confirmed
  HVAC: 21,              // 3 weeks — equipment + contractor coordination
  INSULATION: 14,        // 2 weeks — batt available, spray foam contractor
  DRYWALL: 14,           // 2 weeks — sheets, mud, tape commodity
  PAINT: 7,              // 1 week — Sherwin-Williams 1-2 days for color match
  CABINETS: 56,          // 8 weeks — custom cabinets always longest lead
  COUNTERTOPS: 28,       // 4 weeks — templating after cabinets, then fab
  FLOORING: 14,          // 2 weeks — Blu confirmed
  APPLIANCES: 21,        // 3 weeks — standard fast, paneled appliances longer
  LANDSCAPING: 14,       // 2 weeks — plants + materials
  // Takeoff categories already present in the schema comment but not in the table above
  HARDWARE: 14,          // 2 weeks — bath/cabinet hardware usually stocked
  FINISHES: 14,          // 2 weeks — generic finishes bucket
  SITEWORK: 7,           // 1 week — dirt, gravel, pipe typically local
}

export const DEFAULT_LEAD_TIME_DAYS = 14 // fallback when we don't know the category

/**
 * Parse supplier delivery terms into a practical lead-time override in days.
 * We intentionally choose the most conservative value when a range is provided.
 *
 * Examples:
 * - "3-5 business days" -> 5
 * - "2 weeks" -> 14
 * - "24-48 hours" -> 2
 * - "Same day pickup" -> 1
 */
export function parseLeadTimeDays(input?: string | null): number | null {
  if (!input) return null
  const text = input.trim().toLowerCase()
  if (!text) return null

  if (text.includes('same day')) return 1
  if (text.includes('next day')) return 1
  if (text.includes('in stock')) return 1
  if (text.includes('will call')) return 1
  if (text.includes('pickup')) return 1

  const rangeMatch = text.match(/(\d+)\s*[-–]\s*(\d+)\s*(business\s+days?|days?|weeks?|hours?)/)
  if (rangeMatch) {
    const upper = Number(rangeMatch[2])
    const unit = rangeMatch[3]
    if (unit.includes('week')) return upper * 7
    if (unit.includes('hour')) return Math.max(1, Math.ceil(upper / 24))
    return upper
  }

  const singleMatch = text.match(/(\d+)\s*(business\s+days?|days?|weeks?|hours?)/)
  if (singleMatch) {
    const value = Number(singleMatch[1])
    const unit = singleMatch[2]
    if (unit.includes('week')) return value * 7
    if (unit.includes('hour')) return Math.max(1, Math.ceil(value / 24))
    return value
  }

  return null
}

/**
 * Resolve the lead time for a category. If a quote override is provided and positive,
 * it takes precedence (supplier-specific reality beats our defaults).
 */
export function getLeadTime(category: string, quoteOverride?: number | null): number {
  if (quoteOverride && quoteOverride > 0) return quoteOverride
  return DFW_LEAD_TIMES_DAYS[category] ?? DEFAULT_LEAD_TIME_DAYS
}

/**
 * Maps LifecycleTask.task (free-form task name from Gantt) → list of takeoff categories
 * that need to be ordered BEFORE that task starts.
 *
 * Matching strategy: exact match first, then fuzzy substring match against the keys
 * below (case-insensitive). If nothing matches we return [] — no reminder.
 */
export const TASK_TO_CATEGORIES: Record<string, string[]> = {
  // Pre-construction / sitework — no material to pre-order
  'Site Work & Grading': [],
  'Sitework': ['SITEWORK'],

  // Shell
  'Foundation': ['FOUNDATION'],
  'Framing': ['FRAMING'],
  'Roofing': ['ROOFING'],

  // Windows are delivered during/after framing — so we order at framing prep time
  'Windows Installation': ['WINDOWS'],
  'Windows': ['WINDOWS'],

  // Doors
  'Doors': ['DOORS'],

  // MEP rough-in
  'Plumbing Rough-In': ['PLUMBING_ROUGH'],
  'Plumbing Rough': ['PLUMBING_ROUGH'],
  'Electrical Rough-In': ['ELECTRICAL_ROUGH'],
  'Electrical Rough': ['ELECTRICAL_ROUGH'],
  'HVAC Rough-In': ['HVAC'],
  'HVAC': ['HVAC'],

  // Shell close
  'Insulation': ['INSULATION'],
  'Drywall': ['DRYWALL'],

  // Exterior
  'Exterior Siding & Masonry': ['EXTERIOR'],
  'Siding': ['EXTERIOR'],
  'Exterior': ['EXTERIOR'],

  // Interior finishes
  'Cabinets': ['CABINETS'],
  'Countertops': ['COUNTERTOPS'],
  'Flooring': ['FLOORING'],
  'Interior Paint': ['PAINT'],
  'Paint': ['PAINT'],

  // Fixtures
  'Plumbing Fixtures': ['PLUMBING_FIXTURES'],
  'Lighting & Fans': ['LIGHTING_FANS'],
  'Lighting': ['LIGHTING_FANS'],

  // Final
  'Appliances': ['APPLIANCES'],
  'Landscaping & Concrete': ['LANDSCAPING'],
  'Landscaping': ['LANDSCAPING'],
}

/**
 * Resolve the takeoff categories that should be ordered before a task starts.
 * Uses exact match first, then substring match against the key set.
 */
export function getCategoriesForTask(taskName: string): string[] {
  if (!taskName) return []
  if (TASK_TO_CATEGORIES[taskName]) return TASK_TO_CATEGORIES[taskName]

  const lower = taskName.toLowerCase()
  for (const [key, categories] of Object.entries(TASK_TO_CATEGORIES)) {
    const keyLower = key.toLowerCase()
    if (lower.includes(keyLower) || keyLower.includes(lower)) {
      return categories
    }
  }
  return []
}

/**
 * Human-readable labels for each category, localized.
 * Used by the Calendar to render event titles like "📦 Pedir: Ventanas".
 */
export const CATEGORY_LABELS_ES: Record<string, string> = {
  FOUNDATION: 'Cimentación',
  FRAMING: 'Estructura',
  ROOFING: 'Techos',
  EXTERIOR: 'Exterior / Revestimiento',
  WINDOWS: 'Ventanas',
  DOORS: 'Puertas',
  PLUMBING_ROUGH: 'Plomería Rough',
  PLUMBING_FIXTURES: 'Plomería Acabados',
  ELECTRICAL_ROUGH: 'Eléctrico Rough',
  LIGHTING_FANS: 'Iluminación',
  HVAC: 'HVAC',
  INSULATION: 'Aislamiento',
  DRYWALL: 'Tablaroca',
  PAINT: 'Pintura',
  CABINETS: 'Gabinetes',
  COUNTERTOPS: 'Encimeras',
  FLOORING: 'Pisos',
  APPLIANCES: 'Electrodomésticos',
  LANDSCAPING: 'Jardinería',
  HARDWARE: 'Herrajes',
  FINISHES: 'Acabados',
  SITEWORK: 'Obra Exterior',
}

export const CATEGORY_LABELS_EN: Record<string, string> = {
  FOUNDATION: 'Foundation',
  FRAMING: 'Framing',
  ROOFING: 'Roofing',
  EXTERIOR: 'Exterior / Siding',
  WINDOWS: 'Windows',
  DOORS: 'Doors',
  PLUMBING_ROUGH: 'Plumbing Rough',
  PLUMBING_FIXTURES: 'Plumbing Fixtures',
  ELECTRICAL_ROUGH: 'Electrical Rough',
  LIGHTING_FANS: 'Lighting & Fans',
  HVAC: 'HVAC',
  INSULATION: 'Insulation',
  DRYWALL: 'Drywall',
  PAINT: 'Paint',
  CABINETS: 'Cabinets',
  COUNTERTOPS: 'Countertops',
  FLOORING: 'Flooring',
  APPLIANCES: 'Appliances',
  LANDSCAPING: 'Landscaping',
  HARDWARE: 'Hardware',
  FINISHES: 'Finishes',
  SITEWORK: 'Sitework',
}
